import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader, Star } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { subscriptionService } from '../../services/subscriptionService';
import { useBusinessContext } from '../../contexts/BusinessContext';

const PaymentDetailsStep = ({ plan, billingInterval, onBack, onSuccess }) => {
  const { isFoundingMember } = useBusinessContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Normalize plan name — backend uses "Pro" but frontend display sometimes uses "Professional"
  const isPro = plan.name === 'Pro' || plan.name === 'Professional';

  // Founder pricing applies only to Pro tier + monthly billing
  const founderRate = 10.00;
  const isFounderPricingApplicable = isFoundingMember && isPro && billingInterval === 'monthly';

  const standardPrice = billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  const price = isFounderPricingApplicable ? founderRate : standardPrice;

  useEffect(() => {
    initializePayment();
  }, []);

  const initializePayment = async () => {
    try {
      setLoading(true);
      
      const planName = plan.name.charAt(0).toUpperCase() + plan.name.slice(1);
      const billingPeriod = billingInterval.charAt(0).toUpperCase() + billingInterval.slice(1);

      const session = await subscriptionService.createCheckoutSession(planName, billingPeriod);
      
      // Initialize Stripe
      const stripe = await loadStripe(session.publishableKey);
      setStripePromise(stripe);
      setClientSecret(session.clientSecret);
      
    } catch (err) {
      console.error('Error initializing payment:', err);
      setError(err.response?.data?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Setting up your checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
            <p className="text-red-800">{error}</p>
          </div>
          <button
            onClick={onBack}
            className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="inline h-5 w-5 mr-2" />
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return null;
  }

  const options = {
    clientSecret,
    onComplete: () => {
      // Navigate to success page when payment completes
      window.location.href = '/subscription-success';
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-slide-up">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left: Embedded Checkout */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={onBack}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Plans
              </button>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Payment Details
              </h2>
              <p className="text-gray-600">
                Complete your {plan.name} subscription
              </p>
            </div>

            {/* Stripe Embedded Checkout */}
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-6 border border-gray-200 sticky top-24">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>
            
            <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">
                    {plan.name} Plan
                    {isFounderPricingApplicable && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                        <Star size={10} fill="currentColor" />
                        Founder
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600 capitalize">Billed {billingInterval}</p>
                </div>
                <div className="text-right">
                  {isFounderPricingApplicable && (
                    <p className="text-xs text-gray-400 line-through">
                      ${standardPrice}
                    </p>
                  )}
                  <p className="font-bold text-gray-900">${price}</p>
                </div>
              </div>

              {isFounderPricingApplicable && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                  <p className="text-xs text-amber-800 font-semibold flex items-center gap-1">
                    🔒 Locked for life — will never increase
                  </p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
  <p className="text-sm font-semibold text-blue-900 mb-1">Secure Checkout</p>
  <p className="text-sm text-blue-800">Your subscription starts immediately upon payment.</p>
</div>

            <div className="text-center text-xs text-gray-500">
              🔒 Secure • PCI Compliant • 🇺🇸 Veteran Owned
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsStep;